import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, unwrapObject } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { cn } from "@/lib/utils";
import { dateLocale } from "@/lib/dateTime";

interface FullLead {
  id: string;
  first_name?: string;
  last_name?: string;
  company_name?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  city?: string;
  state?: string;
  source?: string;
  priority?: string;
  lead_status: string;
  expected_budget?: number;
  next_follow_up_at?: string | null;
  do_not_call?: boolean;
  owner?: { first_name: string; last_name?: string } | null;
  created_on?: string;
}

const name = (l: FullLead) => [l.first_name, l.last_name].filter(Boolean).join(" ") || l.company_name || "Lead";
const ROWS: [string, (l: FullLead) => string | number | null | undefined][] = [
  ["Company", (l) => l.company_name],
  ["Email", (l) => l.email],
  ["Phone", (l) => l.mobile || l.phone],
  ["City", (l) => [l.city, l.state].filter(Boolean).join(", ")],
  ["Source", (l) => l.source],
  ["Priority", (l) => l.priority],
  ["Status", (l) => l.lead_status],
  ["Budget", (l) => (Number(l.expected_budget) > 0 ? Number(l.expected_budget).toLocaleString("en-IN") : "")],
  ["Owner", (l) => (l.owner ? [l.owner.first_name, l.owner.last_name].filter(Boolean).join(" ") : "")],
  ["Do not call", (l) => (l.do_not_call ? "Yes" : "")],
  ["Created", (l) => (l.created_on ? new Date(l.created_on).toLocaleDateString(dateLocale()) : "")],
];

/**
 * Two leads side by side: choose the one to keep. Empty fields on it are filled from the other, everything linked to the other
 * (tasks, activity, WhatsApp chats, enquiries, samples, history) moves over, and the other is removed. A converted lead is always the one kept.
 */
export function LeadMergeDialog({ open, onClose, ids, onMerged }: { open: boolean; onClose: () => void; ids: [string, string] | null; onMerged: (keptId: string) => void }) {
  const { success, error } = useToast();
  const [leads, setLeads] = useState<FullLead[]>([]);
  const [keep, setKeep] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !ids) return;
    let live = true;
    setLoading(true);
    setLeads([]);
    Promise.all(ids.map((i) => api.get(`/api/Lead/ById/${i}`).then((r) => unwrapObject<FullLead>(r))))
      .then((rows) => {
        if (!live) return;
        setLeads(rows);
        // A converted lead has to be the one kept; otherwise the first one given
        setKeep((rows.find((r) => r.lead_status === "CONVERTED") || rows[0])?.id || "");
      })
      .catch((e) => live && error("Couldn't load the leads", e?.message || "Try again"))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [open, ids?.[0], ids?.[1]]); // eslint-disable-line react-hooks/exhaustive-deps

  const converted = leads.find((l) => l.lead_status === "CONVERTED");
  const both = leads.filter((l) => l.lead_status === "CONVERTED").length === 2;
  const other = leads.find((l) => l.id !== keep);
  const kept = leads.find((l) => l.id === keep);

  const submit = async () => {
    if (!keep || !other) return;
    setBusy(true);
    try {
      await api.post("/api/Lead/Merge", { keep_id: keep, merge_id: other.id });
      success("Leads merged", `"${name(other)}" is now part of "${name(kept!)}".`);
      onClose();
      onMerged(keep);
    } catch (e: any) {
      error("Could not merge", e?.message || "Try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Merge these two leads</DialogTitle>
          <DialogDescription>Choose the lead to keep. The other one is removed after everything on it moves across. This cannot be undone.</DialogDescription>
        </DialogHeader>
        {loading && <p className="py-6 text-center text-sm text-muted-foreground">Loading…</p>}
        {leads.length === 2 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="w-24" />
                  {leads.map((l) => {
                    const forced = !!converted && l.lead_status !== "CONVERTED";
                    return (
                      <th key={l.id} className="p-1 text-left align-top">
                        <label className={cn("flex cursor-pointer items-start gap-2 rounded-md border p-2", keep === l.id ? "border-primary bg-primary/5" : "border-border", forced && "cursor-not-allowed opacity-60")}>
                          <input type="radio" name="keep" className="mt-1 accent-primary" checked={keep === l.id} disabled={forced} onChange={() => setKeep(l.id)} />
                          <span className="min-w-0">
                            <span className="block truncate font-semibold">{name(l)}</span>
                            <span className="text-xs font-normal text-primary">{keep === l.id ? "Keep this one" : "Merge into the other"}</span>
                          </span>
                        </label>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {ROWS.map(([label, get]) => {
                  const a = get(leads[0]!);
                  const b = get(leads[1]!);
                  if (!a && !b) return null;
                  const fill = (side: FullLead, mine: unknown, theirs: unknown) => !mine && !!theirs && side.id === keep;
                  return (
                    <tr key={label} className="border-t border-border/60">
                      <td className="py-1.5 pr-2 text-xs text-muted-foreground">{label}</td>
                      {[leads[0]!, leads[1]!].map((l, i) => {
                        const mine = i === 0 ? a : b;
                        const theirs = i === 0 ? b : a;
                        return (
                          <td key={l.id} className={cn("px-2 py-1.5", fill(l, mine, theirs) && "text-muted-foreground")}>
                            {mine || <span className="text-muted-foreground">{fill(l, mine, theirs) ? `← ${theirs}` : "—"}</span>}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p className="mt-3 rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
              Empty fields on the lead you keep are filled from the other. The hotter priority and the higher score win, a "do not call" on either is kept, and its tasks, notes, chats, enquiries and history move over.
            </p>
            {both && <p className="mt-2 text-xs text-destructive">Both leads are converted; their accounts and deals can't be merged here.</p>}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="destructive" disabled={busy || loading || leads.length !== 2 || both} onClick={submit}>
            {busy ? "Merging…" : "Merge leads"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
